import { toast } from 'vue-sonner'
import { quotesService } from '../../services/index.js'
import { quoteEditorService } from '../../services/quoteEditorService.js'
import { calculateTotalFromPriceLines } from '../../utils/quoteUtils.js'
import { useApi } from '../useApi.js'
import { useQuotePDFGeneration } from './useQuotePDFGeneration.js'
import { useQuotePDFPreview } from './useQuotePDFPreview.js'
import { useQuotePDFEmail } from './useQuotePDFEmail.js'

/**
 * Composable pour gérer les PDF de devis
 * Limite: 80 lignes max
 */
export function useQuotePDF(quoteId, quote, callbacks = {}) {
  const { updateStatus, loadQuote, loadHistory } = callbacks
  const { put: apiPut } = useApi()
  const {
    exporting,
    showAmountModal,
    quoteAmountInput,
    isFromEditor,
    generateQuotePDF: baseGenerateQuotePDF,
    generatePDFPreview: baseGeneratePDFPreview
  } = useQuotePDFGeneration(quoteId, quote)

  const {
    showPDFPreview,
    pdfPreviewUrl,
    openPreview,
    closePDFPreview,
    downloadPDF
  } = useQuotePDFPreview(quoteId, quote)

  const {
    sendingQuoteEmail,
    sendQuoteByEmail: baseSendQuoteByEmail,
    generateAndSendQuote
  } = useQuotePDFEmail(quoteId, quote, { updateStatus, loadQuote, loadHistory })

  const generatePDFPreview = async (amount, custom = false) => {
    const url = await baseGeneratePDFPreview(amount, custom)
    if (url) {
      openPreview(url)
    }
  }

  const generateQuotePDF = async () => {
    await baseGenerateQuotePDF(openPreview)
  }

  const sendQuoteByEmail = async (payload = {}) => {
    try {
      const success = await baseSendQuoteByEmail(payload)
      if (success) {
        closePDFPreview()
      }
      return success
    } catch (err) {
      throw err
    }
  }

  const updateFirstLineWithAmount = async (editorData, amount) => {
    const calculatedTotal = calculateTotalFromPriceLines(editorData?.prix?.lignes || [])
    
    if (calculatedTotal === 0 && editorData?.prix?.lignes?.length > 0) {
      editorData.prix.lignes[0].prixUnitaire = amount
      editorData.prix.lignes[0].quantite = 1
      
      // ?source=amount-update : cet appel n'est pas une édition de devis mais
      // l'injection du montant confirmé dans la première ligne. Sans ce marqueur,
      // le journal enregistrait « Devis édité » alors que l'éditeur n'a jamais
      // été ouvert. Le corps reste l'editorData persisté, intact.
      await apiPut(`/api/quotes/${quoteId}/editor-data?source=amount-update`, editorData, {
        errorMessage: 'Erreur lors de la mise à jour des lignes du devis'
      })
    }
  }

  const updateQuoteAmount = async (amount) => {
    const data = await quotesService.update(quoteId, {
      ...quote.value,
      quote_amount: amount
    })
    quote.value = data
  }

  const confirmAmountAndGenerate = async (amount = null) => {
    const finalAmount = amount !== null ? amount : parseFloat(quoteAmountInput.value)
    
    if (amount !== null) {
      quoteAmountInput.value = finalAmount.toString()
    }
    
    if (!quoteAmountInput.value || isNaN(finalAmount) || finalAmount < 0) {
      toast.error('Veuillez saisir un montant valide')
      return
    }
    
    try {
      if (isFromEditor.value) {
        try {
          const editorData = await quoteEditorService.getEditorData(quoteId)
          await updateFirstLineWithAmount(editorData, finalAmount)
        } catch (editorErr) {
          console.warn('Erreur lors de la mise à jour de l\'éditeur, continuation avec la mise à jour du montant:', editorErr)
        }
      }
      
      await updateQuoteAmount(finalAmount)
      showAmountModal.value = false
      
      const useCustom = isFromEditor.value
      isFromEditor.value = false
      await generatePDFPreview(finalAmount, useCustom)
    } catch (err) {
      toast.error('Erreur lors de la mise à jour du montant')
      console.error(err)
      throw err
    }
  }

  return {
    exporting,
    showAmountModal,
    quoteAmountInput,
    showPDFPreview,
    pdfPreviewUrl,
    sendingQuoteEmail,
    isFromEditor,
    generateQuotePDF,
    generatePDFPreview,
    sendQuoteByEmail,
    downloadPDF,
    closePDFPreview,
    confirmAmountAndGenerate,
    generateAndSendQuote
  }
}

